import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'star-rating',
  templateUrl: './star-rating.component.html',
  styleUrls: ['./star-rating.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StarRatingComponent),
      multi: true
    }
  ],
  standalone: false
})
export class StarRatingComponent implements ControlValueAccessor {
  private _rating = 0;

  @Output() public ratingChanged = new EventEmitter<number>();

  @Input() public disabled = false;
  @Input() public activeColor = '#488aff';
  @Input() public defaultColor = '#f4f4f4';
  @Input() public activeIcon = 'star';
  @Input() public defaultIcon = 'star-outline';
  @Input() public halfIcon = 'star-half';
  @Input() public halfStar: string | boolean = false;
  @Input() public readonly: string | boolean = false;
  @Input() public fontSize = '24px';

  @Input()
  public set maxRating(value: number) {
    const parsed = Number(value);
    this._maxRating = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 5;
  }
  public get maxRating(): number {
    return this._maxRating;
  }

  @Input()
  public set rating(value: number | string | null | undefined) {
    this._rating = this.normalizeRating(value);
  }
  public get rating(): number {
    return this._rating;
  }

  private _maxRating = 5;

  private onChange: (value: number) => void = () => {};
  private onTouched: () => void = () => {};

  public get stars(): number[] {
    return Array.from({ length: this.maxRating }, (_, index) => index + 1);
  }

  public get isReadonly(): boolean {
    return this.coerceBoolean(this.readonly);
  }

  public writeValue(value: number): void {
    this._rating = this.normalizeRating(value);
  }

  public registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public changeRating(value: number): void {
    if (this.disabled || this.isReadonly) {
      return;
    }

    this._rating = this.normalizeRating(value);
    this.onTouched();
    this.onChange(this._rating);
    this.ratingChanged.emit(this._rating);
  }

  public iconFor(star: number): string {
    const fullStars = Math.floor(this._rating);
    const hasHalf = this.coerceBoolean(this.halfStar) && this._rating % 1 >= 0.5;

    if (star <= fullStars) {
      return this.activeIcon;
    }

    if (hasHalf && star === fullStars + 1) {
      return this.halfIcon;
    }

    return this.defaultIcon;
  }

  public colorFor(star: number): string {
    const fullStars = Math.floor(this._rating);
    const hasHalf = this.coerceBoolean(this.halfStar) && this._rating % 1 >= 0.5;
    if (star <= fullStars || (hasHalf && star === fullStars + 1)) {
      return this.activeColor;
    }
    return this.defaultColor;
  }

  private normalizeRating(value: number | string | null | undefined): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Math.max(0, Math.min(this.maxRating, parsed));
  }

  private coerceBoolean(value: string | boolean | null | undefined): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (value === null || value === undefined) {
      return false;
    }
    return value !== 'false';
  }
}
